import { Icon, Flex, Text, Image, Card, Skeleton } from "components/common"
import React, { useState, useCallback, useMemo, memo } from "react"
import { FaSpinner } from "react-icons/fa"
import cx from "clsx"
import styles from "./index.module.less"
import {t} from "i18next"

const IMAGE_CONSTANTS = {
  BORDER_RADIUS: "inherit"
}

function WaImage({ parsedContent }) {
  const [isLoading, setIsLoading] = useState(true) // Start with loading true
  const [hasError, setHasError] = useState(false)
  const [isImageLoaded, setIsImageLoaded] = useState(false)

  const imageData = useMemo(() => ({
    url: parsedContent?.s3KeyValue || "",
    thumbnail: parsedContent?.thumbnail || parsedContent?.s3KeyValue || "",
    mimeType: parsedContent?.mimeType || "image/jpeg",
    caption: parsedContent?.caption || ""
  }), [parsedContent])

  const handleImageLoad = useCallback(() => {
    setIsImageLoaded(true)
    setIsLoading(false)
    setHasError(false)
  }, [])

  const handleImageError = useCallback(() => {
    setHasError(true)
    setIsLoading(false)
    setIsImageLoaded(false)
  }, [])

  const handleImageLoadStart = useCallback(() => {
    setIsLoading(true)
    setHasError(false)
  }, [])

  if (!imageData.url) {
    return (
      <Card className={styles.mediaCard}>
        <Flex 
          align="center" 
          justify="center" 
          className={cx(styles.errorState, styles.imageUnavailable)}
        >
          <Icon icon="HiPhoto" className={cx(styles.errorIcon, styles.unavailable)} />
          <Text type="secondary">{t("Image unavailable")}</Text>
        </Flex>
      </Card>
    )
  }

  if (hasError) {
    return (
      <Card className={styles.mediaCard}>
        <Flex 
          align="center" 
          justify="center" 
          className={cx(styles.errorState, styles.imageError)}
        >
          <Icon icon="HiExclamationTriangle" className={cx(styles.errorIcon, styles.error)} />
          <Text type="secondary" className={cx(styles.errorText, styles.error)}>{t("Failed to load image")}</Text>
        </Flex>
      </Card>
    )
  }

  return (
    <Card className={styles.mediaCard}>
      <div className={styles.imageContainer}>
        {isLoading && !isImageLoaded && (
          <Skeleton 
            active
            type="image"
            style={{
              width: "100%",
              height: "200px",
              borderRadius: "14px",
              minWidth: "280px"
            }}
          />
        )}
        <Image
          src={imageData.url} 
          // preview={true}
          onLoad={handleImageLoad}
          onError={handleImageError}
          onLoadStart={handleImageLoadStart}
          style={{
            width: "100%",
            height: "auto",
            maxHeight: "300px",
            objectFit: "cover",
            borderRadius: IMAGE_CONSTANTS.BORDER_RADIUS,
            display: isImageLoaded ? "block" : "none",
            minWidth: "280px"
          }}
        />
        {isLoading && isImageLoaded && (
          <div className={styles.loadingOverlay}>
            <FaSpinner 
              size={28} 
              style={{ 
                color: "white",
                animation: "spin 1s linear infinite"
              }} 
            />
          </div>
        )}
      </div>
    </Card>
  )
}

const MemoizedWaImage = memo(WaImage)
MemoizedWaImage.displayName = 'WaImage'

export default MemoizedWaImage